import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { formatHoursMinutes } from "@/utils/dateUtils";
import { formatWindowRange } from "@/utils/incentive";
import type { IncentivePlan } from "@/utils/models";

type Props = {
	windows: IncentivePlan[];
	// Shift post time, used to figure out which window is currently active.
	postedAt?: DateTime;
	// Hours remaining in the active window (from the backend), shown in parentheses on that row.
	hoursLeft?: number | null;
};

// Bonus decay breakdown for a tooltip: one row per window (e.g. "0–24h: $70"), with the window
// we're currently in bolded and annotated with the time left, e.g. "0–24h: $70 (3h left)".
export const IncentiveCurveBreakdown: FC<Props> = ({ windows, postedAt, hoursLeft }) => {
	const { t } = useTranslation();
	const sorted = [...windows].sort((a, b) => a.startHour - b.startHour);
	const elapsedHours = postedAt ? DateTime.now().diff(postedAt, "hours").hours : null;
	const activeIdx = elapsedHours == null ? -1 : sorted.findIndex((w) => w.startHour <= elapsedHours && (w.endHour == null || elapsedHours < w.endHour));

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
			{sorted.map((w, i) => {
				const isActive = i === activeIdx;
				const timeLeft = isActive && hoursLeft != null ? ` (${t("incentive.hours.left.short", { duration: formatHoursMinutes(hoursLeft) })})` : "";
				return (
					<Typography key={`${w.startHour}-${w.endHour ?? "open"}`} sx={{ fontSize: "0.75rem", fontWeight: isActive ? 700 : 400 }}>
						{`${formatWindowRange(w)}: $${w.value}${timeLeft}`}
					</Typography>
				);
			})}
		</Box>
	);
};
